import { FeedbackButton as HubWidget } from '@refractspaces/feedback-widget/react';
import { useRef } from 'react';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { toContext, toReporter } from '../../lib/feedback-hub.js';
import type { FeedbackViewContext, SessionEmployee } from '../../lib/feedback-hub.js';
import './feedback.css';

/**
 * The Refract feedback hub's button, wearing this product's wiring.
 *
 * Rendered instead of the native `FeedbackButton` whenever
 * `VITE_FEEDBACK_PROJECT_KEY` is set (see `app.tsx`). The form, the
 * vocabularies, the outbox, the retry ladder and the redaction all live in
 * `@refractspaces/feedback-widget` — the standardised part that makes a report
 * from this product readable next to one from any other. What is ours is the
 * wiring: who is reporting, what they were looking at, and the trail of what
 * they did, which `lib/diagnostics.ts` already narrates and forwards.
 *
 * `reporter` and `context` read through a ref at submit time, not at mount —
 * this is a shared till, and the person at the keyboard changes every few
 * minutes. Capturing at mount would file every afternoon report as whoever
 * opened the drawer that morning.
 */
export function HubFeedbackButton({
  employee,
  context,
}: {
  employee: SessionEmployee;
  context: FeedbackViewContext;
}) {
  const live = useRef({ employee, context });
  live.current = { employee, context };

  return (
    <HubWidget
      projectKey={import.meta.env.VITE_FEEDBACK_PROJECT_KEY!}
      reporter={() => toReporter(live.current.employee)}
      context={() => toContext(live.current.context)}
      appVersion={import.meta.env.VITE_APP_VERSION ?? 'dev'}
      label="Feedback"
      onSubmitted={(reference) =>
        recordBreadcrumb('feedback', reference === null ? 'sent a report' : `sent ${reference}`)
      }
    />
  );
}
